import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Card,
    CardContent,
} from "@mui/material";
import { PieChart, Pie, Tooltip, Cell, Legend, ResponsiveContainer } from "recharts";
import axios from "axios";

const API_INVENTORY = "https://figazuresql.swancreate.com/api/swa/vw_CurrentInventoryCounts";

const START_DATE = new Date("2024-06-30");
const END_DATE = new Date("2025-07-31");

const PRIMARY_COLORS = [
    "rgba(0, 255, 255, 0.8)", // Electric Cyan
    "rgba(0, 128, 0, 0.8)",   // Electric Green
    "rgba(64, 224, 208, 0.8)",// Turquoise
    "rgba(70, 130, 180, 0.8)",// Steel Blue
    "rgba(0, 191, 255, 0.8)", // Deep Sky Blue
    "rgba(100, 149, 237, 0.8)", // Cornflower Blue
];

const InventoryCountsByCategory = () => {
    const [inventoryData, setInventoryData] = useState([]);
    const [selectedDistrict, setSelectedDistrict] = useState("All");
    const [selectedSite, setSelectedSite] = useState("All");
    const [selectedMonth, setSelectedMonth] = useState("All");
    const [months, setMonths] = useState([]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await axios.get(API_INVENTORY);
            setInventoryData(response.data);

            const uniqueMonths = [
                ...new Set(
                    response.data
                        .filter((item) => {
                            const itemDate = new Date(`${item.Year}-${String(item.Month).padStart(2, "0")}-01`);
                            return itemDate >= START_DATE && itemDate <= END_DATE;
                        })
                        .map((item) => `${item.Year}-${String(item.Month).padStart(2, "0")}`)
                ),
            ].sort();
            setMonths(uniqueMonths);
        } catch (err) {
            console.error("Error fetching inventory data:", err);
        }
    };

    const distinctDistricts = [
        "All",
        ...new Set(inventoryData.map((item) => item.DistrictName || "Unknown District")),
    ];

    const distinctSites = [
        "All",
        ...new Set(
            inventoryData
                .filter((item) =>
                    selectedDistrict === "All"
                        ? true
                        : item.DistrictName === selectedDistrict
                )
                .map((item) => item.SiteName || "Unknown Site")
        ),
    ];

    const filteredData = inventoryData
        .filter((item) =>
            selectedDistrict === "All" ? true : item.DistrictName === selectedDistrict
        )
        .filter((item) =>
            selectedSite === "All" ? true : item.SiteName === selectedSite
        )
        .filter((item) => {
            const itemDate = new Date(`${item.Year}-${String(item.Month).padStart(2, "0")}-01`);
            return itemDate >= START_DATE && itemDate <= END_DATE;
        })
        .filter((item) =>
            selectedMonth === "All"
                ? true
                : `${item.Year}-${String(item.Month).padStart(2, "0")}` === selectedMonth
        );

    const groupedByCategory = filteredData.reduce((acc, item) => {
        const category = item.InventoryCategoryName || "Unknown Category";
        if (!acc[category]) {
            acc[category] = { UNITS: 0, TotalCost: 0, TotalValue: 0 };
        }
        acc[category].UNITS += item.UNITS || 0;
        acc[category].TotalCost += item.TotalCost || 0;
        acc[category].TotalValue += item.TotalValue || 0;
        return acc;
    }, {});

    const districtTotals = filteredData.reduce(
        (acc, item) => {
            acc.units += item.UNITS || 0;
            acc.totalCost += item.TotalCost || 0;
            acc.totalValue += item.TotalValue || 0;
            return acc;
        },
        { units: 0, totalCost: 0, totalValue: 0 }
    );

    const statBoxData = Object.entries(groupedByCategory).map(([category, values], index) => ({
        name: category,
        units: values.UNITS,
        totalCost: values.TotalCost,
        totalValue: values.TotalValue,
        color: PRIMARY_COLORS[index % PRIMARY_COLORS.length],
    }));

    return (
        <Box
            m="20px"
            sx={{
                backgroundColor: "#121212",
                color: "white",
                minHeight: "100vh",
                padding: "20px",
                borderRadius: "10px",
            }}
        >
            <Typography variant="h4" gutterBottom>
                NASA-Themed Inventory Dashboard
            </Typography>
            <Box mb="20px">
                <Card
                    sx={{
                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                        color: "white",
                        border: "1px solid #00FFFF",
                        borderRadius: "10px",
                    }}
                >
                    <CardContent>
                        <Typography variant="h5" color="#00FFFF" gutterBottom>
                            District Totals
                        </Typography>
                        <Typography variant="body1">
                            Total Units: <strong>{districtTotals.units}</strong>
                        </Typography>
                        <Typography variant="body1">
                            Total Cost: <strong>${districtTotals.totalCost.toFixed(2)}</strong>
                        </Typography>
                        <Typography variant="body1">
                            Total Value: <strong>${districtTotals.totalValue.toFixed(2)}</strong>
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(240px, 1fr))" gap="20px" mb="20px">
                {statBoxData.map(({ name, units, totalCost, color }) => (
                    <Card
                        key={name}
                        sx={{
                            backgroundColor: color,
                            opacity: 0.9,
                            borderRadius: "10px",
                            boxShadow: "0px 0px 20px 2px rgba(0, 255, 255, 0.5)",
                        }}
                    >
                        <CardContent>
                            <Typography variant="h6">{name}</Typography>
                            <Typography variant="body1">Units: {units}</Typography>
                            <Typography variant="body1">Total Cost: ${totalCost.toFixed(2)}</Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            <Box display="flex" gap="20px">
                <ResponsiveContainer width="50%" height={400}>
                    <PieChart>
                        <Pie
                            data={statBoxData}
                            dataKey="totalCost"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={150}
                            label={(entry) => `$${entry.totalCost.toFixed(2)}`}
                        >
                            {statBoxData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                        <Legend formatter={(value) => value} />
                    </PieChart>
                </ResponsiveContainer>

                <TableContainer
                    component={Paper}
                    sx={{
                        width: "50%",
                        backgroundColor: "#1E1E1E",
                        color: "white",
                        borderRadius: "10px",
                        boxShadow: "0px 0px 15px rgba(0, 255, 255, 0.5)",
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ color: "#00FFFF" }}>Category</TableCell>
                                <TableCell align="right" style={{ color: "#00FFFF" }}>
                                    Units
                                </TableCell>
                                <TableCell align="right" style={{ color: "#00FFFF" }}>
                                    Total Cost
                                </TableCell>
                                <TableCell align="right" style={{ color: "#00FFFF" }}>
                                    Total Value
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {statBoxData.map(({ name, units, totalCost, totalValue }, index) => (
                                <TableRow
                                    key={name}
                                    sx={{
                                        backgroundColor: index % 2 === 0 ? "rgba(0,255,255,0.1)" : "transparent",
                                    }}
                                >
                                    <TableCell style={{ color: "white" }}>{name}</TableCell>
                                    <TableCell align="right" style={{ color: "white" }}>
                                        {units}
                                    </TableCell>
                                    <TableCell align="right" style={{ color: "white" }}>
                                        ${totalCost.toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right" style={{ color: "white" }}>
                                        ${totalValue.toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default InventoryCountsByCategory;
